import { useContext } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import { API } from '../api/endpoints';
import { useConnectionStore } from '../stores/connectionStore';
import { useGlobalStore } from '../stores/globalStore';
import type { SecurityRole } from '../types/user';

/**
 * How the API applies the staged desktop role levels on this connection
 * (docs/roadmap/web-permission-enforcement.md): `Off` skips them, `Audit` lets every request
 * through and logs what it would refuse, `Enforce` refuses with 403.
 */
export type PermissionMode = 'Off' | 'Audit' | 'Enforce';

/** `GET api/User/Me/PermissionEnforcement`. Anything unreadable counts as Audit (today's UI). */
export function parsePermissionMode(raw: unknown): PermissionMode {
  const mode = typeof raw === 'object' && raw !== null ? (raw as { mode?: unknown }).mode : raw;
  if (typeof mode !== 'string') return 'Audit';
  const m = mode.trim().toLowerCase();
  if (m === 'enforce') return 'Enforce';
  if (m === 'off') return 'Off';
  return 'Audit';
}

export const permissionEnforcementKeys = {
  mode: (connectionId: number | string | undefined, userId: number) =>
    ['permission-enforcement', connectionId ?? 0, userId] as const,
};

/**
 * The caller's connection mode. The website hides or locks a staged section ONLY when this says
 * Enforce; while it loads, after an error, and in Audit/Off the page stays as it is today (the API
 * is the real check, so a failed lookup never opens anything the server would refuse).
 */
export function usePermissionEnforcement(): { mode: PermissionMode; enforced: boolean; loaded: boolean; settled: boolean } {
  return usePermissionModeQuery(undefined);
}

/**
 * Used only by useOptionalPermissionEnforcement outside a QueryClientProvider: its query is off,
 * so the mode reads Audit.
 */
const NO_PROVIDER_CLIENT = new QueryClient({ defaultOptions: { queries: { enabled: false, retry: false } } });

/**
 * The same answer as usePermissionEnforcement, for shared code that is also rendered without a
 * QueryClientProvider (the navigation, which some component tests mount bare): there it reads
 * Audit instead of throwing. Inside the app it is the same cached lookup.
 */
export function useOptionalPermissionEnforcement(): { mode: PermissionMode; enforced: boolean; loaded: boolean; settled: boolean } {
  const contextClient = useContext(QueryClientContext);
  return usePermissionModeQuery(contextClient ? undefined : NO_PROVIDER_CLIENT);
}

function usePermissionModeQuery(client: QueryClient | undefined) {
  const connectionId = useConnectionStore((s) => s.selectedConnection?.connectionId);
  const userId = useGlobalStore((s) => s.userId);
  const { data, isSuccess, isError } = useQuery({
    queryKey: permissionEnforcementKeys.mode(connectionId, userId),
    queryFn: async () => {
      const { data } = await apiClient.get<unknown>(API.userMePermissionEnforcement);
      return parsePermissionMode(data);
    },
    enabled: userId > 0 && client !== NO_PROVIDER_CLIENT,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  }, client);
  const mode: PermissionMode = isSuccess && data ? data : 'Audit';
  // `settled`: the lookup has answered or failed, so `mode` will not change any more. A page that
  // WRITES on its own (not on a click) waits for it, so it never fires a write Enforce refuses.
  return { mode, enforced: mode === 'Enforce', loaded: isSuccess, settled: isSuccess || isError };
}

/** A role column as a number; a missing role or column reads 0 (no access). */
export function roleLevel(role: SecurityRole | null | undefined, field: keyof SecurityRole): number {
  if (!role) return 0;
  const v = (role as unknown as Record<string, unknown>)[field as string];
  return typeof v === 'number' ? v : 0;
}

/**
 * A staged rule for the UI: true when the role meets `field >= min`, or when the connection is not
 * enforced (Audit / Off keep today's UI). Mirrors the API's [StagedRoleLevel] rules.
 */
export function stagedAllows(enforced: boolean, role: SecurityRole | null | undefined, field: keyof SecurityRole, min: number): boolean {
  return !enforced || roleLevel(role, field) >= min;
}

/** One rule of an "any of" staged rule: the role column and its minimum. */
export type StagedAnyRule = readonly [field: keyof SecurityRole, min: number];

/**
 * An "any of" staged rule (the API's two- to four-rule [StagedRoleLevel] forms): true when the role
 * meets at least one rule, or when the connection is not enforced.
 */
export function stagedAllowsAny(enforced: boolean, role: SecurityRole | null | undefined, rules: readonly StagedAnyRule[]): boolean {
  return !enforced || rules.some(([field, min]) => roleLevel(role, field) >= min);
}

/**
 * `allows(field, min)`: see {@link stagedAllows}; `allowsAny(rules)`: see {@link stagedAllowsAny}. `enforced` is exposed for pages that need to
 * branch (e.g. read-only rather than hidden).
 */
export function useStagedPermission() {
  const { enforced, mode, settled } = usePermissionEnforcement();
  const role = useGlobalStore((s) => s.userRole);
  return {
    enforced,
    mode,
    settled,
    role,
    allows: (field: keyof SecurityRole, min: number) => stagedAllows(enforced, role, field, min),
    allowsAny: (rules: readonly StagedAnyRule[]) => stagedAllowsAny(enforced, role, rules),
  };
}
